import './AssignPlaylistOverlay.scss';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import { BRAND_LETTER } from '../../js/config/_global';

import { useTranslation } from 'react-i18next';
import {
    ORDER_BY_DIRECTION,
    RESPONSE,
    SEARCH_DEBOUNCE_VALUE
} from '../../js/_constants';
import {
    sanitizeCollectionResponse
} from '../../js/helpers';
import { type Playlist, type Device } from '../../api/types/index';

import InputField from '../inputField/InputField';
import ItemCard from '../ItemCard/ItemCard';
import { type CollectionResponse } from '../../api/types/response';
import { useFetch } from '../../hooks/useFetch';
import _server from '../../js/config/_server';
import { type SanitizedCollection } from '../../js/helpers';
import Loader from '../loader/Loader';
import debounce from 'debounce';
import { isEmpty } from 'ramda';
import { getIri } from '../../js/fpUtils';

type AssignPlaylistOverlayProps = {
    device: Device;
    isFilterable: boolean;
    onItemClick: (playlistIri: string) => void;
    selectedPlaylists: Playlist[];
    market?: boolean;

};

type SanitizerFunction = (
    collection: CollectionResponse<Playlist>
) => SanitizedCollection<Playlist>;

const AssignPlaylistOverlay = ({
    device,
    isFilterable,
    onItemClick,
    selectedPlaylists = [],
    market
}: AssignPlaylistOverlayProps) => {
    const { t } = useTranslation();

    const [pendingIri, setPendingIri] = useState('');

    const {
        data: playlists,
        isLoading,
        refetchData
    } = useFetch<CollectionResponse<Playlist>, SanitizerFunction>(
        _server.services.playlists.userPlaylists,
        {
            params: {
                createdByCurrentUser: market ? false : true,
                brand: BRAND_LETTER,
                'order[updatedAt]': ORDER_BY_DIRECTION.DESC
            },
            dataFormatter: (collection: CollectionResponse<Playlist>) =>
                sanitizeCollectionResponse<Playlist>(collection)
        }
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        refetchData({
            params: {
                // filterKvps: device.dealership.kvps,
                createdByCurrentUser: market ? false : true,
                brand: BRAND_LETTER,
                'order[updatedAt]': ORDER_BY_DIRECTION.DESC,
                ...(value && {title: value})
            },
            dataFormatter: (collection: CollectionResponse<Playlist>) =>
                sanitizeCollectionResponse<Playlist>(collection)
        });
    };

    const debouncedHandleSearch = debounce(handleSearch, SEARCH_DEBOUNCE_VALUE);

    const handleAddPlaylistToDevice = playlistIri => onItemClick(playlistIri);

    const isSelected = item =>
        selectedPlaylists.findIndex(
            selectedPlaylist =>
                selectedPlaylist.contentPlaylist[RESPONSE.ID] === item[RESPONSE.ID]
        ) > -1;

    const handlePlaylistAssignment = (itemIri: string) => {
        setPendingIri(itemIri);
        handleAddPlaylistToDevice(itemIri);
    };

    const handlePlaylistCardClick = (playlistId: string) => {
        handlePlaylistAssignment(playlistId);
    };

    const matchesWithDeviceModuleTypes = (playlist: Playlist) =>
        playlist.moduleTypes.some(plModuleType =>
            device.moduleTypes.map(getIri).includes(plModuleType)
        );

    useEffect(() => {
        setPendingIri('');
    }, [selectedPlaylists]);

    const renderPlaylists = () => {
        const compatiblePlaylists =
            playlists?.list.filter(matchesWithDeviceModuleTypes) ?? [];

        if (isEmpty(compatiblePlaylists)) {
            return (
                <span className="empty-playlists">
                    {t('device_detail_playlist_overlay_no_playlists')}
                </span>
            );
        }
        return compatiblePlaylists.map((item, index) => {
            const itemClasses = classNames({
                'is-selected': isSelected(item),
                'playlist-card': true
            });
            return (
                <li key={item[RESPONSE.ID]}>
                    <ItemCard
                        name={`assign-playlist-card-${index + 1}`}
                        key={item[RESPONSE.ID]}
                        className={itemClasses}
                        thumbnail={item.thumbnail}
                        icon={isSelected(item) ? "select-circle" : "media-playlist" }
                        title={item.title}
                        isLoading={pendingIri === item[RESPONSE.ID]}
                        onClick={() =>
                            handlePlaylistCardClick(item[RESPONSE.ID])
                        }
                    />
                </li>
            );
        });
    };

    return (
        <div
            className={classNames('b-assign-playlist-overlay', {
                'is-loading': !!pendingIri
            })}
        >
            {isFilterable && (
                <InputField
                    label={t('searchInput')}
                    name="assign-playlist-search"
                    onChangeHandler={debouncedHandleSearch}
                />
            )}
            <ul className="playlists-list">
                {isLoading ? <Loader /> : renderPlaylists()}
            </ul>
        </div>
    );
};

export default AssignPlaylistOverlay;
